var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { authorizeManageMissionClassifiers } from '../../../../backend/features/permissions/shared';
import { renderCreatePositionDialog } from '../../../../ui/components/CreatePositionDialog';
import { renderShiftDurationInfo } from '../../../../ui/components/ShiftDurationInfo';
import { useMissionPositionStorage, useMissionsTranslation } from '../../../../ui/hooks';
import { requireAnySystemPermission } from '@juvare/contact-management/backend/features/permissions/shared';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { addDays, endOfDay } from 'date-fns';
import { useCallback } from 'react';
import { getShiftDaysOptions } from '../utils';
import { repeatShiftInDateRange } from '../utils/repeatShiftInDateRange';
const oneMinuteInMs = 60000;
const oneHourInMs = oneMinuteInMs * 60;
export const useShiftWizardSteps = (positionsData, wizardActionContext) => {
    const { t } = useMissionsTranslation();
    const displayAddressPermissions = ['fullAddress', 'cityOnly', 'countyOnly'];
    const missionsPositionStorage = useMissionPositionStorage();
    const userState = useUserState();
    const timeValuesProvider = useCallback(() => {
        const step = 30;
        const times = [];
        let startTime = 0;
        for (let i = 0; startTime < 24 * 60; i++) {
            const hours = Math.floor(startTime / 60);
            const minutes = startTime % 60;
            const value = hours * oneHourInMs + minutes * oneMinuteInMs;
            if (startTime !== 0) {
                const hourString = hours !== 0 ? `${('' + hours).slice(-2)} ${t('common.hours')}` : '';
                const minuteString = minutes !== 0 ? `${('' + minutes).slice(-2)} ${t('common.minutes')}` : '';
                times.push({
                    label: `${hourString} ${minuteString}`,
                    value,
                });
            }
            startTime = startTime + step;
        }
        return times;
    }, [t]);
    const buildDisplayAddressPermissionValues = () => {
        return displayAddressPermissions.map((permission) => ({
            label: t(`shifts.form.${permission}`),
            value: permission,
        }));
    };
    const customPositionHandler = () => __awaiter(void 0, void 0, void 0, function* () {
        const items = yield missionsPositionStorage.list({
            filter: [{ column: 'status', op: '=', value: 'active' }],
            orderBy: [{ column: 'name' }],
        });
        return getPositionFieldValues(items.rows);
    });
    const step1 = {
        label: t('shifts.form.dateAndTime'),
        form: {
            type: 'form',
            children: [
                {
                    type: 'form.panel',
                    key: '!formPanel',
                    children: [
                        {
                            key: 'startTime',
                            type: 'form.dateTimePicker',
                            timeFormat: '12h',
                            timeMinuteStep: 15,
                            label: t('shifts.form.shiftStartDate'),
                            timeLabel: t('shifts.form.startTime'),
                            dateLabel: t('shifts.form.shiftStartDate'),
                            validate: { required: true },
                        },
                        {
                            key: 'duration',
                            type: 'form.select',
                            label: t('shifts.form.duration'),
                            values: [...timeValuesProvider()],
                            validate: { required: true },
                            style: { width: 380 },
                        },
                    ],
                    style: {
                        layout: 'row',
                        horizontalAlign: 'left',
                    },
                },
                {
                    type: 'form.content',
                    key: 'content',
                    render: (handler) => {
                        var _a, _b, _c, _d;
                        const startTime = (_b = (_a = handler.up().down('!formPanel')) === null || _a === void 0 ? void 0 : _a.down('startTime')) === null || _b === void 0 ? void 0 : _b.getData();
                        const duration = (_d = (_c = handler.up().down('!formPanel')) === null || _c === void 0 ? void 0 : _c.down('duration')) === null || _d === void 0 ? void 0 : _d.getData();
                        return renderShiftDurationInfo({ startTime, duration });
                    },
                },
                ...((wizardActionContext === null || wizardActionContext === void 0 ? void 0 : wizardActionContext.isEdit) || (wizardActionContext === null || wizardActionContext === void 0 ? void 0 : wizardActionContext.isDuplicate)
                    ? []
                    : [
                        {
                            type: 'form.checkbox',
                            key: 'repeatShift',
                            label: t('shifts.form.repeatShift'),
                            style: {
                                type: 'switch',
                            },
                        },
                        {
                            type: 'form.panel',
                            label: t('shifts.form.repeatShift'),
                            children: [
                                {
                                    type: 'form.checkboxList',
                                    key: 'shiftDays',
                                    values: getShiftDaysOptions(t),
                                    style: {
                                        inline: true,
                                    },
                                    validate: {
                                        requiredMessage: t('errors.atLeastOneWeekdayMustBeSelected'),
                                        required: true,
                                    },
                                },
                            ],
                            hidden: {
                                when: '^repeatShift',
                                dataNotEq: true,
                            },
                        },
                        {
                            type: 'form.panel',
                            label: t('shifts.form.until'),
                            children: [
                                {
                                    type: 'form.datePicker',
                                    key: 'endDate',
                                    label: t('shifts.form.endDate'),
                                    style: {
                                        width: 316,
                                    },
                                    validate: {
                                        custom: (value, { formData }) => {
                                            if (!value) {
                                                return undefined;
                                            }
                                            if (formData.startTime) {
                                                const minEndDate = addDays(formData.startTime, 1);
                                                if (minEndDate > endOfDay(new Date(value))) {
                                                    return t('errors.endDateMustBeAfterTheStartDate');
                                                }
                                            }
                                            const shifts = repeatShiftInDateRange(formData);
                                            if (shifts.length > 100) {
                                                return t('errors.maximumRepeatingShifts', { maxShifts: 100 });
                                            }
                                            return undefined;
                                        },
                                        required: true,
                                    },
                                },
                            ],
                            hidden: {
                                when: '^repeatShift',
                                dataNotEq: true,
                            },
                        },
                    ]),
            ],
        },
        description: t('shifts.form.dateAndTimeDescription'),
    };
    const editShiftFormWidgetConfigs = [
        {
            key: 'additionalPositions',
            type: 'form.dataGrid',
            addButtonLabel: t('shifts.form.additionalPosition'),
            validate: {
                minLength: 0,
            },
            children: [
                {
                    key: 'positionId',
                    type: 'form.select',
                    label: t('shifts.form.choosePosition'),
                    valueProvider: {
                        custom: customPositionHandler,
                    },
                    validate: {
                        required: true,
                        uniqueInList: true,
                        custom: (value) => {
                            const positionIsUsed = positionsData === null || positionsData === void 0 ? void 0 : positionsData.some((position) => {
                                return position.positionId === value;
                            });
                            if (positionIsUsed && value) {
                                return t('errors.alreadyUsed');
                            }
                            return undefined;
                        },
                    },
                },
                {
                    key: 'quantity',
                    type: 'form.number',
                    label: t('shifts.form.quantity'),
                    validate: {
                        required: true,
                        min: 1,
                    },
                    style: { width: 168 },
                },
            ],
        },
        {
            type: 'form.content',
            key: 'content',
            text: t('shifts.form.editShiftPositionDescription'),
            style: {
                alert: 'info',
            },
        },
    ];
    const createPositionWidgetConfig = [
        {
            key: 'divider',
            type: 'form.divider',
        },
        {
            key: 'createPosition',
            type: 'form.content',
            render: (handler) => renderCreatePositionDialog({ handler, isEdit: wizardActionContext === null || wizardActionContext === void 0 ? void 0 : wizardActionContext.isEdit }),
        },
    ];
    const step2 = {
        label: t('shifts.form.positions'),
        form: {
            type: 'form',
            children: [
                {
                    key: 'positions',
                    type: 'form.dataGrid',
                    addButtonLabel: t('shifts.form.additionalPosition'),
                    validate: {
                        required: true,
                        minLength: (wizardActionContext === null || wizardActionContext === void 0 ? void 0 : wizardActionContext.isEdit) ? positionsData === null || positionsData === void 0 ? void 0 : positionsData.length : 1,
                        maxLength: (wizardActionContext === null || wizardActionContext === void 0 ? void 0 : wizardActionContext.isEdit) ? positionsData === null || positionsData === void 0 ? void 0 : positionsData.length : undefined,
                    },
                    style: { paddingRight: (wizardActionContext === null || wizardActionContext === void 0 ? void 0 : wizardActionContext.isDuplicate) ? 0 : 72 },
                    children: [
                        {
                            key: 'positionId',
                            type: 'form.select',
                            label: (wizardActionContext === null || wizardActionContext === void 0 ? void 0 : wizardActionContext.isEdit) ? t('shifts.form.position') : t('shifts.form.choosePosition'),
                            valueProvider: {
                                custom: customPositionHandler,
                            },
                            disabled: wizardActionContext === null || wizardActionContext === void 0 ? void 0 : wizardActionContext.isEdit,
                            validate: {
                                required: true,
                                uniqueInList: true,
                            },
                        },
                        {
                            key: 'quantity',
                            type: 'form.number',
                            label: t('shifts.form.quantity'),
                            validate: {
                                required: true,
                                min: 1,
                                custom: (value, handler) => {
                                    var _a;
                                    if ((wizardActionContext === null || wizardActionContext === void 0 ? void 0 : wizardActionContext.isEdit) && positionsData) {
                                        const positionId = (_a = handler.up().down('positionId')) === null || _a === void 0 ? void 0 : _a.getData();
                                        const validatePosition = (position) => {
                                            return position.positionId === positionId;
                                        };
                                        const positionIsUsed = positionsData.some(validatePosition);
                                        const { quantity } = positionsData.filter(validatePosition)[0];
                                        if (positionIsUsed && quantity && value) {
                                            if (value < quantity) {
                                                return t('shifts.form.quantityCantBeLowered');
                                            }
                                        }
                                    }
                                    return undefined;
                                },
                            },
                            style: { width: (wizardActionContext === null || wizardActionContext === void 0 ? void 0 : wizardActionContext.isDuplicate) ? 168 : 160 },
                        },
                    ],
                },
                ...((wizardActionContext === null || wizardActionContext === void 0 ? void 0 : wizardActionContext.isEdit) ? editShiftFormWidgetConfigs : []),
                ...(authorizeManageMissionClassifiers(userState) ||
                    requireAnySystemPermission(userState, 'manage-mission-position-classifier')
                    ? createPositionWidgetConfig
                    : []),
            ],
        },
        description: t('shifts.form.positionsDescription'),
    };
    const step3 = {
        label: t('shifts.form.checkInAddress'),
        form: {
            type: 'form',
            children: [
                {
                    key: 'checkInAddress',
                    type: 'form.radio',
                    style: {
                        inline: true,
                        paddingLeft: 11,
                    },
                    defaultValue: 'existingAddress',
                    values: [
                        {
                            label: t('shifts.form.useExistingAddress'),
                            value: 'existingAddress',
                        },
                        {
                            label: t('shifts.form.addNewAddress'),
                            value: 'newAddress',
                        },
                    ],
                },
                {
                    type: 'form.select',
                    key: 'checkInAddressId',
                    label: t('shifts.form.selectAddress'),
                    valueProvider: {
                        type: 'classifier.missionLocation',
                    },
                    hidden: {
                        when: '^checkInAddress',
                        dataNotEq: 'existingAddress',
                    },
                    validate: {
                        required: true,
                    },
                    clearDataOnHide: true,
                    showDescription: true,
                },
                {
                    type: 'form.panel',
                    key: 'addNewLocation',
                    children: [
                        {
                            type: 'form.panel',
                            children: [
                                {
                                    type: 'form.text',
                                    key: 'name',
                                    label: t('shifts.form.name'),
                                    validate: {
                                        required: true,
                                    },
                                },
                                {
                                    type: 'form.select',
                                    key: 'type',
                                    label: t('shifts.form.locationType'),
                                    validate: {
                                        required: true,
                                    },
                                    valueProvider: {
                                        type: 'classifier.locationType',
                                    },
                                },
                            ],
                            style: {
                                layout: 'row',
                                horizontalAlign: 'left',
                            },
                        },
                        {
                            type: 'form.text',
                            key: 'address',
                            label: t('shifts.form.addressLine'),
                            validate: {
                                required: true,
                            },
                        },
                        {
                            type: 'form.text',
                            key: 'address2',
                            label: t('shifts.form.addressLine2'),
                        },
                        {
                            type: 'form.panel',
                            children: [
                                {
                                    type: 'form.text',
                                    key: 'city',
                                    label: t('shifts.form.city'),
                                    validate: {
                                        required: true,
                                    },
                                },
                                {
                                    type: 'form.text',
                                    key: 'zip',
                                    label: t('shifts.form.zip'),
                                    validate: {
                                        required: true,
                                        regex: '\\d{5}',
                                    },
                                },
                            ],
                            style: {
                                layout: 'row',
                                horizontalAlign: 'left',
                            },
                        },
                        {
                            type: 'form.panel',
                            children: [
                                {
                                    type: 'form.select',
                                    key: 'state',
                                    label: t('shifts.form.state'),
                                    validate: {
                                        required: true,
                                    },
                                    valueProvider: { type: 'availableMissionLocationStates' },
                                },
                                {
                                    type: 'form.select',
                                    key: 'county',
                                    label: t('shifts.form.county'),
                                    validate: {
                                        required: true,
                                    },
                                    disabled: { whenNoValues: true },
                                    valueProvider: { type: 'classifier.county', paramsFrom: '^state' },
                                },
                            ],
                            style: {
                                layout: 'row',
                                horizontalAlign: 'left',
                            },
                        },
                    ],
                    style: {
                        horizontalAlign: 'left',
                    },
                    hidden: {
                        when: '^checkInAddress',
                        dataNotEq: 'newAddress',
                    },
                    clearDataOnHide: true,
                },
            ],
        },
        description: t('shifts.form.checkInAddressDescription'),
    };
    const step4 = {
        label: t('shifts.form.shiftContact'),
        form: {
            type: 'form',
            children: [
                {
                    key: 'contactName',
                    type: 'form.text',
                    label: t('shifts.form.contactName'),
                    validate: { required: true },
                },
                {
                    key: 'contactEmail',
                    type: 'form.email',
                    label: t('shifts.form.contactEmail'),
                    validate: { required: true },
                },
                {
                    key: 'contactPhone',
                    type: 'form.phone',
                    label: t('shifts.form.contactPhone'),
                    validate: { required: true },
                },
            ],
        },
        description: t('shifts.form.shiftContactDescription'),
    };
    const step5 = {
        form: {
            type: 'form',
            children: [
                {
                    type: 'form.label',
                    template: { content: `<h6>${t('shifts.form.displayShiftCheckIn')}</h6>` },
                    component: {
                        type: 'form.radio',
                        key: 'permission',
                        defaultValue: 'fullAddress',
                        values: buildDisplayAddressPermissionValues(),
                        validate: { required: true },
                        style: {
                            paddingTop: 8,
                            inline: true,
                        },
                    },
                },
                {
                    key: 'displayShiftContact',
                    type: 'form.checkbox',
                    label: t('shifts.form.displayShiftContact'),
                    defaultValue: false,
                    style: {
                        type: 'switch',
                    },
                },
                {
                    key: 'displayNumberOfPositions',
                    type: 'form.checkbox',
                    label: t('shifts.form.displayNumberOfPositionsNeeded'),
                    defaultValue: false,
                    style: {
                        type: 'switch',
                    },
                },
                {
                    type: 'form.content',
                    key: 'content',
                    text: t('shifts.form.shiftContentDescription'),
                    style: {
                        alert: 'info',
                    },
                },
            ],
        },
        label: t('shifts.form.permissions'),
        description: t('shifts.form.permissionsDescription'),
    };
    return { shiftWizardSteps: [step1, step2, step3, step4, step5] };
};
export const getPositionFieldValues = (positions = []) => {
    return positions.reduce((values, position) => {
        return [
            ...values,
            {
                value: position.id,
                label: position.columns.name,
            },
        ];
    }, []);
};
